import { FUNCTION_BASE } from "../lib/constants";
import { authFetch } from "../lib/api";
import React, { useState, useEffect, useRef, useCallback } from "react";
import Cropper from "react-easy-crop";
import {
  FaFacebook, FaInstagram, FaEye, FaLink, FaComments, FaClipboardList,
  FaShoppingCart, FaMobileAlt, FaImage, FaVideo, FaImages, FaCloudUploadAlt,
  FaCheckCircle, FaLock, FaEdit, FaExclamationTriangle, FaCheck, FaBullseye, FaBookmark,
  FaChevronLeft, FaChevronRight,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../lib/firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/Wizard.css";

const steps = [
  { id: 1, title: "Campaign" },
  { id: 2, title: "Audience" },
  { id: 3, title: "Ad Creative" },
  { id: 4, title: "Budget" },
  { id: 5, title: "Review" },
];

const campaignObjectives = [
  { id: "OUTCOME_AWARENESS",     label: "Awareness",    icon: <FaEye />,           description: "Show your ad to people most likely to remember it" },
  { id: "OUTCOME_TRAFFIC",       label: "Traffic",      icon: <FaLink />,          description: "Drive clicks to your website or landing page" },
  { id: "OUTCOME_ENGAGEMENT",    label: "Engagement",   icon: <FaComments />,      description: "Get more likes, comments, shares, and interactions" },
  { id: "OUTCOME_LEADS",         label: "Leads",        icon: <FaClipboardList />, description: "Collect contact info through lead forms" },
  { id: "OUTCOME_SALES",         label: "Sales",        icon: <FaShoppingCart />,  description: "Drive purchases on your website or app" },
  { id: "OUTCOME_APP_PROMOTION", label: "App Promotion", icon: <FaMobileAlt />,    description: "Get more people to discover and install your app" },
];

const adFormats = [
  { id: "SINGLE_IMAGE", label: "Single Image", icon: <FaImage />,  description: "One image with text" },
  { id: "SINGLE_VIDEO", label: "Single Video", icon: <FaVideo />,  description: "One video with text" },
  { id: "CAROUSEL",     label: "Carousel",     icon: <FaImages />, description: "Multiple images or videos users can swipe through" },
];

const ctaOptions = [
  { id: "LEARN_MORE",  label: "Learn More" },
  { id: "SHOP_NOW",    label: "Shop Now" },
  { id: "SIGN_UP",     label: "Sign Up" },
  { id: "CONTACT_US",  label: "Contact Us" },
  { id: "BOOK_NOW",    label: "Book Now" },
  { id: "DOWNLOAD",    label: "Download" },
  { id: "GET_OFFER",   label: "Get Offer" },
  { id: "GET_QUOTE",   label: "Get Quote" },
  { id: "SUBSCRIBE",   label: "Subscribe" },
  { id: "WATCH_MORE",  label: "Watch More" },
];

const genderOptions = [
  { id: "all",    label: "All" },
  { id: "male",   label: "Male" },
  { id: "female", label: "Female" },
];

const CreateCampaignWizard = ({ onClose, initialData }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1
  const [campaignTitle, setCampaignTitle] = useState("");
  const [adGroupName, setAdGroupName] = useState("");
  const [selectedObjective, setSelectedObjective] = useState("");
  const [platforms, setPlatforms] = useState({ facebook: true, instagram: false });

  // Step 2
  const [ageRange, setAgeRange] = useState({ min: 18, max: 65 });
  const [gender, setGender] = useState("all");
  const [locations, setLocations] = useState([]);
  const [locationInput, setLocationInput] = useState("");
  const [locationResults, setLocationResults] = useState([]);
  const [searchingLocations, setSearchingLocations] = useState(false);
  const [locationSearchTimer, setLocationSearchTimer] = useState(null);
  const [interests, setInterests] = useState([]);
  const [interestInput, setInterestInput] = useState("");
  const [audienceType, setAudienceType] = useState("new");
  const [savedAudiences, setSavedAudiences] = useState([]);
  const [loadingAudiences, setLoadingAudiences] = useState(false);
  const [selectedSavedAudienceId, setSelectedSavedAudienceId] = useState("");

  // Step 3
  const [adFormat, setAdFormat] = useState("SINGLE_IMAGE");
  const [adMedia, setAdMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const videoRef = useRef(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [headline, setHeadline] = useState("");
  const [primaryText, setPrimaryText] = useState("");
  const [description, setDescription] = useState("");
  const [ctaButton, setCtaButton] = useState("LEARN_MORE");
  const [destinationUrl, setDestinationUrl] = useState("");

  // Step 4
  const [budgetType, setBudgetType] = useState("daily");
  const [campaignBudget, setCampaignBudget] = useState(10);
  const [campaignStartDate, setCampaignStartDate] = useState("");
  const [campaignEndDate, setCampaignEndDate] = useState("");
  // Store dates in LOCAL time so the picker displays exactly what the user selected
  const toLocalISO = (date) => {
    const p = n => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${p(date.getMonth()+1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
  };
  // Convert local ISO back to UTC ISO string for the backend
  const toUTCISO = (localISO) => localISO ? new Date(localISO).toISOString() : "";

  // Status
  const [statusMessage, setStatusMessage] = useState("");
  const [fbCampaignId, setFbCampaignId] = useState(null);
  const [fbAdSetId, setFbAdSetId] = useState(null);
  const [fbCreativeId, setFbCreativeId] = useState(null);
  const [fbAdId, setFbAdId] = useState(null);

  // Account
  const [fbAccounts, setFbAccounts] = useState({});
  const [fbPages, setFbPages] = useState([]);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [selectedFbAccountId, setSelectedFbAccountId] = useState("");
  const [selectedPageId, setSelectedPageId] = useState("");
  const [selectedIgAccountId, setSelectedIgAccountId] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionRenewalDate, setSubscriptionRenewalDate] = useState(null);
  const [tokenType, setTokenType] = useState(null);
  const [draftBanner, setDraftBanner] = useState(null);

  // Interest search
  const [interestResults, setInterestResults] = useState([]);
  const [searchingInterests, setSearchingInterests] = useState(false);
  const [interestSearchTimer, setInterestSearchTimer] = useState(null);

  const [isPublishing, setIsPublishing] = useState(false);

  const resetWizard = () => {
    localStorage.removeItem("wizardDraft");
    setCurrentStep(1);
    setCampaignTitle(""); setAdGroupName(""); setSelectedObjective("");
    setPlatforms({ facebook: true, instagram: false });
    setAgeRange({ min: 18, max: 65 }); setGender("all");
    setLocations([]); setInterests([]); setAudienceType("new"); setSelectedSavedAudienceId("");
    setAdFormat("SINGLE_IMAGE"); setAdMedia(null); setMediaPreview(null);
    setHeadline(""); setPrimaryText(""); setDescription("");
    setCtaButton("LEARN_MORE"); setDestinationUrl("");
    setBudgetType("daily"); setCampaignBudget(10);
    setCampaignStartDate(""); setCampaignEndDate("");
    setDraftBanner(null);
  };

  useEffect(() => {
    if (initialData) {
      // Pre-fill from copied campaign — skip localStorage
      localStorage.removeItem("wizardDraft");
      setCampaignTitle(`${initialData.name || "Campaign"} Copy`);
      setAdGroupName(initialData.adGroupName || "");
      setSelectedObjective(initialData.objective || "");
      if (initialData.platforms) setPlatforms(initialData.platforms);
      setAgeRange({ min: initialData.ageMin || 18, max: initialData.ageMax || 65 });
      setGender(initialData.gender || "all");
      try { setLocations(typeof initialData.locations === "string" ? JSON.parse(initialData.locations) : (initialData.locations || [])); } catch { setLocations([]); }
      try { setInterests(typeof initialData.interests === "string" ? JSON.parse(initialData.interests) : (initialData.interests || [])); } catch { setInterests([]); }
      setAdFormat(initialData.adFormat || "SINGLE_IMAGE");
      setHeadline(initialData.headline || "");
      setPrimaryText(initialData.primaryText || "");
      setDescription(initialData.description || "");
      setCtaButton(initialData.ctaButton || "LEARN_MORE");
      setDestinationUrl(initialData.destinationUrl || "");
      setBudgetType(initialData.budgetType || (initialData.lifetime_budget ? "lifetime" : "daily"));
      setCampaignBudget(initialData.campaignBudget || initialData.budget || 10);
      return;
    }
    try {
      const saved = localStorage.getItem("wizardDraft");
      if (saved) {
        const d = JSON.parse(saved);
        if (d.campaignTitle || d.currentStep > 1) {
          setDraftBanner({ title: d.campaignTitle || "Untitled Campaign", step: d.currentStep || 1 });
        }
        if (d.campaignTitle) setCampaignTitle(d.campaignTitle);
        if (d.adGroupName) setAdGroupName(d.adGroupName);
        if (d.selectedObjective) setSelectedObjective(d.selectedObjective);
        if (d.platforms) setPlatforms(d.platforms);
        if (d.ageRange) setAgeRange(d.ageRange);
        if (d.gender) setGender(d.gender);
        if (d.locations) setLocations(d.locations);
        if (d.interests) setInterests(d.interests);
        if (d.audienceType) setAudienceType(d.audienceType);
        if (d.selectedSavedAudienceId) setSelectedSavedAudienceId(d.selectedSavedAudienceId);
        if (d.adFormat) setAdFormat(d.adFormat);
        if (d.headline) setHeadline(d.headline);
        if (d.primaryText) setPrimaryText(d.primaryText);
        if (d.description) setDescription(d.description);
        if (d.ctaButton) setCtaButton(d.ctaButton);
        if (d.destinationUrl) setDestinationUrl(d.destinationUrl);
        if (d.budgetType) setBudgetType(d.budgetType);
        if (d.campaignBudget) setCampaignBudget(d.campaignBudget);
        if (d.campaignStartDate) setCampaignStartDate(d.campaignStartDate);
        if (d.selectedFbAccountId) setSelectedFbAccountId(d.selectedFbAccountId);
        if (d.selectedIgAccountId) setSelectedIgAccountId(d.selectedIgAccountId);
      }
    } catch {}
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      localStorage.setItem("wizardDraft", JSON.stringify({
        currentStep, campaignTitle, adGroupName, selectedObjective, platforms,
        ageRange, gender, locations, interests, audienceType, selectedSavedAudienceId,
        adFormat, headline, primaryText, description, ctaButton, destinationUrl,
        budgetType, campaignBudget, campaignStartDate,
        selectedFbAccountId, selectedIgAccountId,
      }));
    } catch {}
  }, [
    currentStep, campaignTitle, adGroupName, selectedObjective, platforms,
    ageRange, gender, locations, interests, audienceType, selectedSavedAudienceId,
    adFormat, headline, primaryText, description, ctaButton, destinationUrl,
    budgetType, campaignBudget, campaignStartDate,
    selectedFbAccountId, selectedIgAccountId,
  ]);

  useEffect(() => {
    if (audienceType === "saved" && savedAudiences.length === 0) {
      const fetchSavedAudiences = async () => {
        if (!auth.currentUser) return;
        setLoadingAudiences(true);
        try {
          const snap = await getDocs(collection(db, "users", auth.currentUser.uid, "savedAudiences"));
          const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          setSavedAudiences(list);
        } catch {}
        finally { setLoadingAudiences(false); }
      };
      fetchSavedAudiences();
    }
  }, [audienceType]); // eslint-disable-line

  const applySavedAudience = (aud) => {
    setSelectedSavedAudienceId(aud.id);
    setAgeRange({ min: aud.ageMin || 18, max: aud.ageMax || 65 });
    setGender(aud.gender === "Male" ? "male" : aud.gender === "Female" ? "female" : "all");
    setLocations(aud.locations || []);
    setInterests(aud.interests || []);
  };

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!auth.currentUser) return;
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const accounts = data.fbAccounts || {};
        setFbAccounts(accounts);
        const defaultId = data.selectedAdAccount || Object.keys(accounts)[0] || "";
        if (defaultId) setSelectedFbAccountId(defaultId);
        const pages = data.fbPages || [];
        setFbPages(pages);
        const defaultPageId = data.selectedPageId || pages[0]?.id || "";
        if (defaultPageId) setSelectedPageId(defaultPageId);
        const igAccounts = data.instagramAccounts || [];
        setInstagramAccounts(igAccounts);
        if (igAccounts.length > 0) setSelectedIgAccountId(igAccounts[0].id || "");
        setSubscriptionStatus(data.subscriptionStatus || null);
        if (data.currentPeriodEnd) {
          setSubscriptionRenewalDate(data.currentPeriodEnd.toDate ? data.currentPeriodEnd.toDate() : new Date(data.currentPeriodEnd));
        }
        setTokenType(data.fbPages?.[0]?.accessToken ? "page" : "user");
      }
    };
    fetchAccounts();
  }, []);

  const handleMediaUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const reader = new FileReader();
    reader.onloadend = () => {
      if (isVideo) {
        setAdMedia(file); setMediaPreview(reader.result);
        setTrimStart(0); setTrimEnd(0); setVideoDuration(0);
      } else {
        setRawImageSrc(reader.result);
        setCropModalOpen(true);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeMedia = () => {
    setAdMedia(null); setMediaPreview(null); setRawImageSrc(null);
    setTrimStart(0); setTrimEnd(0); setVideoDuration(0);
  };

  const onCropComplete = useCallback((_, pixels) => { setCroppedAreaPixels(pixels); }, []);

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageSrc;
    });
    const canvas = document.createElement("canvas");
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.95));
  };

  const handleCropConfirm = async () => {
    if (!rawImageSrc || !croppedAreaPixels) return;
    const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
    const croppedFile = new File([blob], "cropped.jpg", { type: "image/jpeg" });
    setAdMedia(croppedFile);
    setMediaPreview(URL.createObjectURL(blob));
    setCropModalOpen(false);
    setRawImageSrc(null);
  };

  const searchLocations = (query) => {
    setLocationInput(query);
    if (locationSearchTimer) clearTimeout(locationSearchTimer);
    if (!query.trim()) { setLocationResults([]); return; }
    const timer = setTimeout(async () => {
      if (!auth.currentUser) return;
      setSearchingLocations(true);
      try {
        const params = new URLSearchParams({ userId: auth.currentUser.uid, q: query });
        const res = await authFetch(`${FUNCTION_BASE}/api/fb/location-search?${params}`);
        const data = await res.json();
        if (data.success) setLocationResults(data.locations || []);
      } catch (err) { console.error("Location search failed:", err); }
      finally { setSearchingLocations(false); }
    }, 400);
    setLocationSearchTimer(timer);
  };

  const addLocation = (loc) => {
    if (!locations.find(l => l.key === loc.key)) setLocations(prev => [...prev, loc]);
    setLocationInput(""); setLocationResults([]);
  };

  const removeLocation = (loc) => setLocations(prev => prev.filter(l => l.key !== loc.key));

  const searchInterests = (query) => {
    setInterestInput(query);
    if (interestSearchTimer) clearTimeout(interestSearchTimer);
    if (!query.trim()) { setInterestResults([]); return; }
    const timer = setTimeout(async () => {
      if (!auth.currentUser) return;
      setSearchingInterests(true);
      try {
        const params = new URLSearchParams({
          userId: auth.currentUser.uid,
          q: query,
          ...(selectedFbAccountId ? { adAccountId: selectedFbAccountId } : {})
        });
        const res = await authFetch(`${FUNCTION_BASE}/api/fb/interest-search?${params}`);
        const data = await res.json();
        if (data.success) setInterestResults(data.interests || []);
      } catch (err) { console.error("Interest search failed:", err); }
      finally { setSearchingInterests(false); }
    }, 400);
    setInterestSearchTimer(timer);
  };

  const addInterest = (interest) => {
    if (interest && !interests.find(i => i.id === interest.id)) setInterests([...interests, interest]);
    setInterestInput(""); setInterestResults([]);
  };

  const removeInterest = (interest) => setInterests(interests.filter(i => i.id !== interest.id));

  const nextStep = () => { if (currentStep < steps.length) setCurrentStep(prev => prev + 1); };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(prev => prev - 1); };

  const publishCampaign = async () => {
    if (!auth.currentUser) { setStatusMessage("Error: Not logged in."); return; }
    if (!adMedia) { setStatusMessage("Error: Please upload media for your ad."); return; }
    if (!platforms.facebook && !platforms.instagram) { setStatusMessage("Error: Please select at least one platform."); return; }
    if (!headline) { setStatusMessage("Error: Please add a headline for your ad."); return; }
    if (!primaryText) { setStatusMessage("Error: Please add primary text for your ad."); return; }
    if (!selectedFbAccountId) { setStatusMessage("Error: Please select an ad account in Step 1."); return; }

    setIsPublishing(true);
    setStatusMessage("Creating campaign on Facebook...");

    try {
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      if (!userData.fbAccessToken) {
        setStatusMessage("Please connect your Facebook account on the Dashboard before publishing.");
        setIsPublishing(false);
        return;
      }

      setStatusMessage("Preparing media...");
      const mediaBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(adMedia);
      });

      const response = await authFetch(
        `${FUNCTION_BASE}/api/campaigns/create-campaign`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mediaBase64, mediaType: adMedia.type, mediaName: adMedia.name,
            userId: auth.currentUser.uid, adAccountId: selectedFbAccountId, pageId: selectedPageId,
            name: campaignTitle, adGroupName: adGroupName || `${campaignTitle} - Ad Set`,
            objective: selectedObjective, budget: campaignBudget, budgetType,
            startDate: toUTCISO(campaignStartDate), endDate: toUTCISO(campaignEndDate),
            audienceType, selectedSavedAudienceId,
            ...(audienceType === "saved" ? {} : {
              ageMin: ageRange.min, ageMax: ageRange.max, gender,
              locations: JSON.stringify(locations), interests: JSON.stringify(interests),
            }),
            headline, primaryText, description, ctaButton, destinationUrl, adFormat,
            platformFacebook: String(platforms.facebook),
            platformInstagram: String(platforms.instagram),
          })
        }
      );

      const result = await response.json();
      if (result?.success) {
        setFbCampaignId(result.fbCampaignId || null);
        setFbAdSetId(result.fbAdSetId || null);
        if (result.ads?.[0]) {
          setFbCreativeId(result.ads[0].fbCreativeId || null);
          setFbAdId(result.ads[0].fbAdId || null);
        }
        localStorage.removeItem("wizardDraft");
        setStatusMessage("Campaign Created!");
        setTimeout(() => onClose(), 2000);
      } else {
        const errorDetail = result?.step ? ` (failed at: ${result.step})` : "";
        setStatusMessage("Error: " + (result?.error || "Unknown error") + errorDetail);
      }
    } catch (err) {
      console.error("Error creating campaign:", err);
      setStatusMessage("Error creating campaign: " + err.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const isSubscribed = true; // temporarily disabled during Meta App Review
  const progressPct = ((currentStep - 1) / (steps.length - 1)) * 100;

  return (
    <>
    {/* Crop Modal */}
    {cropModalOpen && rawImageSrc && (
      <div className="wizard-crop-overlay">
        <div className="wizard-crop-modal">
          <h3 className="wizard-crop-title">Crop Image</h3>
          <div className="wizard-crop-area">
            <Cropper
              image={rawImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="wizard-crop-zoom">
            <label className="wizard-crop-zoom-label">Zoom</label>
            <input type="range" min={1} max={3} step={0.05} value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))} style={{ width: "100%" }} />
          </div>
          <div className="wizard-crop-actions">
            <button className="wizard-crop-cancel" onClick={() => { setCropModalOpen(false); setRawImageSrc(null); }}>Cancel</button>
            <button className="wizard-crop-confirm" onClick={handleCropConfirm}>Use Cropped Image</button>
          </div>
        </div>
      </div>
    )}

    <div className="wizard-overlay">
    <div className="wizard-modal-wrap">
    <button className="wizard-modal-close" onClick={onClose}>✕</button>
    <div className="wizard-modal">

      {/* Sticky header */}
      <div className="wizard-modal-header">
        <div className="wizard-modal-title-row">
          <h1 className="wizard-title">{initialData ? "Copy Campaign" : "Create Campaign"}</h1>
        </div>

        {/* Step Nav */}
        <div className="wizard-step-nav">
          <div className="wizard-step-track">
            <div className="wizard-step-fill" style={{ width: `${progressPct}%` }} />
          </div>
          {steps.map((step) => (
            <div key={step.id} className="wizard-step-item" onClick={() => setCurrentStep(step.id)}>
              <div className={`wizard-step-circle${currentStep >= step.id ? " done" : ""}`}>{step.id}</div>
              <div className={`wizard-step-label${currentStep >= step.id ? " done" : ""}`}>{step.title}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="wizard-modal-body">

      {/* Step Content */}
      <div className="wizard-step-content">

        {/* ─── STEP 1: CAMPAIGN ─── */}
        {currentStep === 1 && (
          <div className="wizard-step-body">

            {/* Draft banner */}
            {draftBanner && (
              <div className="wizard-draft-banner">
                <div className="wizard-draft-inner">
                  <span className="wizard-draft-icon"><FaEdit /></span>
                  <div>
                    <div className="wizard-draft-title">Unfinished campaign restored</div>
                    <div className="wizard-draft-desc">"{draftBanner.title}" — your previous answers are pre-filled below</div>
                  </div>
                </div>
                <div className="wizard-draft-actions">
                  <button className="wizard-draft-fresh-btn" onClick={resetWizard}>Start Fresh</button>
                  <button className="wizard-draft-continue-btn" onClick={() => setDraftBanner(null)}>Continue</button>
                </div>
              </div>
            )}

            {/* Ad Account + Facebook Page */}
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
              <div className="wizard-account-box" style={{ flex: 1 }}>
                <label className="wz-label" style={{ marginBottom: "10px" }}>Select Ad Account <span className="wz-req">*</span></label>
                {Object.keys(fbAccounts).length === 0 ? (
                  <div className="wizard-account-warning"><FaExclamationTriangle style={{ marginRight: 6 }} />No ad accounts found. Connect your Facebook account on the Overview first.</div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: "0.85rem" }}>
                      {fbAccounts[selectedFbAccountId]?.name?.[0]?.toUpperCase() || "A"}
                    </div>
                    <select className="wz-input" value={selectedFbAccountId}
                      onChange={(e) => setSelectedFbAccountId(e.target.value)}
                      style={{ background: "white", cursor: "pointer", flex: 1 }}>
                      {Object.entries(fbAccounts).map(([id, account]) => (
                        <option key={id} value={id}>{account.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {selectedFbAccountId && fbAccounts[selectedFbAccountId] && (
                  <div className="wizard-account-hint">
                    {fbAccounts[selectedFbAccountId].hasPaymentMethod ? (
                      <span className="wizard-account-ok"><FaCheck style={{ marginRight: 4 }} />Payment method active</span>
                    ) : (
                      <>
                        <span className="wizard-account-warn"><FaExclamationTriangle style={{ marginRight: 4 }} />No payment method</span>
                        <a className="wizard-account-payment-link"
                          href={`https://business.facebook.com/billing_hub/payment_methods?asset_id=${selectedFbAccountId.replace("act_", "")}`}
                          target="_blank" rel="noopener noreferrer">
                          Add Payment Method
                        </a>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="wizard-account-box" style={{ flex: 1 }}>
                <label className="wz-label" style={{ marginBottom: "10px" }}>Select Facebook Page <span className="wz-req">*</span></label>
                {fbPages.length === 0 ? (
                  <div className="wizard-account-warning"><FaExclamationTriangle style={{ marginRight: 6 }} />No Facebook Pages found. Connect your Facebook account on the Overview first.</div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {(() => {
                      const page = fbPages.find(p => p.id === selectedPageId);
                      return page?.picture
                        ? <img src={page.picture} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "1px solid #e2e8f0" }} />
                        : <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#e2e8f0", flexShrink: 0 }} />;
                    })()}
                    <select className="wz-input" value={selectedPageId}
                      onChange={(e) => setSelectedPageId(e.target.value)}
                      style={{ background: "white", cursor: "pointer", flex: 1 }}>
                      {fbPages.map((page) => (
                        <option key={page.id} value={page.id}>{page.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Platforms */}
            <div>
              <label className="wz-label">Platforms <span className="wz-req">*</span></label>
              <div className="wizard-platform-row">
                <button type="button"
                  className={`wizard-platform-btn${platforms.facebook ? " fb-active" : ""}`}
                  onClick={() => {
                    if (platforms.facebook && !platforms.instagram) return;
                    setPlatforms(prev => ({ ...prev, facebook: !prev.facebook }));
                  }}>
                  <FaFacebook /> Facebook
                  {platforms.facebook && <span className="wizard-platform-check"><FaCheck /></span>}
                </button>
                <button type="button"
                  className={`wizard-platform-btn${platforms.instagram ? " ig-active" : ""}${instagramAccounts.length === 0 ? " disabled" : ""}`}
                  onClick={() => {
                    if (platforms.instagram && !platforms.facebook) return;
                    if (!platforms.instagram && instagramAccounts.length === 0) {
                      alert("No Instagram account connected. Connect one in your dashboard first.");
                      return;
                    }
                    setPlatforms(prev => ({ ...prev, instagram: !prev.instagram }));
                  }}>
                  <FaInstagram /> Instagram
                  {platforms.instagram && <span className="wizard-platform-check"><FaCheck /></span>}
                </button>
              </div>
            </div>

            {/* Campaign Title + Ad Group + Objective — 3-col row */}
            <div className="wizard-campaign-row">
              <div className="wizard-campaign-col">
                <label className="wz-label">Campaign Title <span className="wz-req">*</span></label>
                <input className="wz-input wz-input-full" type="text" value={campaignTitle}
                  onChange={(e) => setCampaignTitle(e.target.value)} />
              </div>
              <div className="wizard-campaign-col">
                <label className="wz-label">Ad Group Name <span className="wz-req">*</span></label>
                <input className="wz-input wz-input-full" type="text" value={adGroupName}
                  onChange={(e) => setAdGroupName(e.target.value)} />
              </div>
              <div className="wizard-campaign-col">
                <label className="wz-label">Campaign Objective <span className="wz-req">*</span></label>
                <select className="wz-input wz-input-full" value={selectedObjective} onChange={e => setSelectedObjective(e.target.value)}>
                  <option value="">Select an objective...</option>
                  {campaignObjectives.map(obj => (
                    <option key={obj.id} value={obj.id}>{obj.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 2: AUDIENCE ─── */}
        {currentStep === 2 && (
          <div className="wizard-step-body">
            {/* Audience type */}
            <div>
              <label className="wz-label">Audience Type</label>
              <div className="wizard-type-grid">
                {[
                  { id: "new",   label: "New Audience",   desc: "Define targeting from scratch" },
                  { id: "saved", label: "Saved Audience",  desc: "Load a saved targeting preset" },
                ].map(type => (
                  <div key={type.id}
                    className={`wizard-type-card${audienceType === type.id ? " selected" : ""}`}
                    onClick={() => setAudienceType(type.id)}>
                    <div className="wizard-type-label">{type.label}</div>
                    <div className="wizard-type-desc">{type.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Saved Audience picker */}
            {audienceType === "saved" && (
              <div>
                <div className="wizard-saved-header">
                  <label className="wz-label" style={{ margin: 0 }}>Select a Saved Audience</label>
                  <a className="wizard-saved-link" href="/dashboard?view=audiences" target="_blank" rel="noopener noreferrer">+ Create new →</a>
                </div>
                {loadingAudiences ? (
                  <div className="wizard-saved-loading">Loading saved audiences...</div>
                ) : savedAudiences.length === 0 ? (
                  <div className="wizard-saved-empty">
                    <div className="wizard-saved-empty-icon"><FaBookmark /></div>
                    <div className="wizard-saved-empty-title">No saved audiences yet</div>
                    <p className="wizard-saved-empty-desc">Go to the Audiences page to save a targeting preset first.</p>
                    <a className="wizard-saved-empty-link" href="/dashboard?view=audiences" target="_blank" rel="noopener noreferrer">Go to Audiences</a>
                  </div>
                ) : (
                  <div className="wizard-saved-list">
                    {savedAudiences.map(aud => {
                      const selected = selectedSavedAudienceId === aud.id;
                      return (
                        <div key={aud.id}
                          className={`wizard-saved-item${selected ? " selected" : ""}`}
                          onClick={() => applySavedAudience(aud)}>
                          <div className="wizard-saved-item-row">
                            <div className="wizard-saved-item-name">{aud.name}</div>
                            <div className="wizard-saved-radio">
                              {selected && <span className="wizard-saved-radio-check"><FaCheck /></span>}
                            </div>
                          </div>
                          {selected && (
                            <div className="wizard-saved-tags">
                              <span className="wizard-saved-tag">Age {aud.ageMin}–{aud.ageMax}</span>
                              <span className="wizard-saved-tag">{aud.gender || "All Genders"}</span>
                              {(aud.locations || []).map(l => <span key={typeof l === "object" ? l.key : l} className="wizard-saved-tag">{typeof l === "object" ? l.name : l}</span>)}
                              {(aud.interests || []).map(i => <span key={typeof i === "object" ? i.id : i} className="wizard-saved-tag">{typeof i === "object" ? i.name : i}</span>)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedSavedAudienceId && (
                  <div className="wizard-saved-applied">Targeting loaded — you can switch to "New Audience" to fine-tune before publishing.</div>
                )}
              </div>
            )}

            {/* New audience targeting */}
            {audienceType === "new" && <>
              {/* Location */}
              <div>
                <label className="wz-label">Locations</label>
                <div className="wizard-search-wrap">
                  <input className="wz-input" type="text" value={locationInput}
                    onChange={(e) => searchLocations(e.target.value)}
                    autoComplete="off" />
                  {(locationResults.length > 0 || searchingLocations) && (
                    <div className="wizard-dropdown">
                      {searchingLocations && <div className="wizard-dropdown-searching">Searching...</div>}
                      {locationResults.map(loc => (
                        <div key={loc.key} className="wizard-dropdown-item" onMouseDown={() => addLocation(loc)}>
                          <span className="wizard-dropdown-name">{loc.name}{loc.region ? `, ${loc.region}` : ""}{loc.country_name ? `, ${loc.country_name}` : ""}</span>
                          <span className="wizard-dropdown-meta">{loc.type}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {locations.length > 0 && (
                  <div className="wizard-tags-row">
                    {locations.map(loc => (
                      <span key={loc.key} className="wizard-tag wizard-tag-blue">
                        {loc.name}{loc.region ? `, ${loc.region}` : ""}
                        <button className="wizard-tag-remove" onClick={() => removeLocation(loc)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Age & Gender */}
              <div className="wizard-age-gender-row">
                <div>
                  <label className="wz-label">Age Range</label>
                  <div className="wizard-age-range">
                    <input className="wz-input" type="number" min="13" max="65" value={ageRange.min}
                      onChange={(e) => setAgeRange({ ...ageRange, min: Number(e.target.value) })}
                      style={{ width: "78px", textAlign: "center" }} />
                    <span className="wizard-age-sep">to</span>
                    <input className="wz-input" type="number" min="13" max="65" value={ageRange.max}
                      onChange={(e) => setAgeRange({ ...ageRange, max: Number(e.target.value) })}
                      style={{ width: "78px", textAlign: "center" }} />
                  </div>
                </div>
                <div>
                  <label className="wz-label">Gender</label>
                  <div className="wizard-gender-row">
                    {genderOptions.map(g => (
                      <button key={g.id} type="button"
                        className={`wizard-gender-btn${gender === g.id ? " selected" : ""}`}
                        onClick={() => setGender(g.id)}>{g.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Interests */}
              <div>
                <label className="wz-label">Interests & Behaviors</label>
                <div className="wizard-search-wrap">
                  <input className="wz-input" type="text" value={interestInput}
                    onChange={(e) => searchInterests(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Escape") { setInterestResults([]); setInterestInput(""); } }}
                    />
                  {searchingInterests && <span className="wizard-searching-inline">Searching...</span>}
                  {interestResults.length > 0 && (
                    <div className="wizard-dropdown">
                      {interestResults.map(result => (
                        <div key={result.id} className="wizard-dropdown-item"
                          onMouseDown={(e) => { e.preventDefault(); addInterest(result); }}>
                          <span className="wizard-dropdown-name">{result.name}</span>
                          {result.audienceSizeLowerBound && (
                            <span className="wizard-dropdown-meta">
                              {(result.audienceSizeLowerBound / 1e6).toFixed(0)}M–{(result.audienceSizeUpperBound / 1e6).toFixed(0)}M people
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {interests.length > 0 && (
                  <div className="wizard-tags-row">
                    {interests.map(interest => (
                      <span key={interest.id} className="wizard-tag wizard-tag-purple">
                        {interest.name}
                        <button className="wizard-tag-remove" onClick={() => removeInterest(interest)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>}
          </div>
        )}

        {/* ─── STEP 3: AD CREATIVE ─── */}
        {currentStep === 3 && (
          <div className="wizard-creative-layout">
            {/* Form */}
            <div className="wizard-creative-form">
              {/* Format */}
              <div>
                <label className="wz-label">Ad Format</label>
                <div className="wizard-format-row">
                  {adFormats.map(format => (
                    <div key={format.id}
                      className={`wizard-format-card${adFormat === format.id ? " selected" : ""}`}
                      onClick={() => setAdFormat(format.id)}>
                      <div className="wizard-format-icon">{format.icon}</div>
                      <div className="wizard-format-label">{format.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Media */}
              <div>
                <label className="wz-label">Media <span className="wz-req">*</span></label>
                {!mediaPreview ? (
                  <label className="wizard-upload-zone">
                    <FaCloudUploadAlt className="wizard-upload-icon" />
                    <span className="wizard-upload-title">Click to upload</span>
                    <span className="wizard-upload-sub">PNG, JPG, GIF, or MP4</span>
                    <input type="file" accept="image/*,video/*" onChange={handleMediaUpload} style={{ display: "none" }} />
                  </label>
                ) : (
                  <div className="wizard-media-preview-wrap">
                    <div style={{ position: "relative" }}>
                      {adMedia?.type?.startsWith("video/") ? (
                        <video ref={videoRef} src={mediaPreview} controls
                          style={{ width: "100%", maxHeight: "200px", objectFit: "cover", display: "block" }}
                          onLoadedMetadata={(e) => { const d = e.target.duration; setVideoDuration(d); setTrimEnd(d); }} />
                      ) : (
                        <img src={mediaPreview} alt="Ad preview" style={{ width: "100%", maxHeight: "200px", objectFit: "cover", display: "block" }} />
                      )}
                      <div className="wizard-media-actions">
                        {!adMedia?.type?.startsWith("video/") && (
                          <button type="button" className="wizard-crop-btn"
                            onClick={() => { setRawImageSrc(mediaPreview); setCropModalOpen(true); }}>Crop</button>
                        )}
                        <button type="button" className="wizard-remove-btn" onClick={removeMedia}>×</button>
                      </div>
                    </div>
                    {adMedia?.type?.startsWith("video/") && videoDuration > 0 && (
                      <div className="wizard-video-trim">
                        <div className="wizard-trim-title">
                          Trim Video — Start: {trimStart.toFixed(1)}s / End: {trimEnd.toFixed(1)}s / Duration: {videoDuration.toFixed(1)}s
                        </div>
                        <div style={{ marginBottom: "6px" }}>
                          <label className="wizard-trim-label">Start</label>
                          <input type="range" min={0} max={trimEnd - 0.5} step={0.1} value={trimStart}
                            onChange={(e) => { const v = parseFloat(e.target.value); setTrimStart(v); if (videoRef.current) videoRef.current.currentTime = v; }}
                            style={{ width: "100%" }} />
                        </div>
                        <div>
                          <label className="wizard-trim-label">End</label>
                          <input type="range" min={trimStart + 0.5} max={videoDuration} step={0.1} value={trimEnd}
                            onChange={(e) => setTrimEnd(parseFloat(e.target.value))}
                            style={{ width: "100%" }} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Primary Text */}
              <div>
                <label className="wz-label">Primary Text <span className="wz-req">*</span></label>
                <textarea className="wz-textarea" value={primaryText}
                  onChange={(e) => setPrimaryText(e.target.value)}
                  maxLength={500} />
                <p className="wz-char-count">{primaryText.length}/500</p>
              </div>

              {/* Headline + Description */}
              <div className="wizard-two-col">
                <div>
                  <label className="wz-label">Headline <span className="wz-req">*</span></label>
                  <input className="wz-input wz-input-full" type="text" value={headline}
                    onChange={(e) => setHeadline(e.target.value)}
                    maxLength={40} />
                  <p className="wz-char-count">{headline.length}/40</p>
                </div>
                <div>
                  <label className="wz-label">Description</label>
                  <input className="wz-input wz-input-full" type="text" value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={30} />
                  <p className="wz-char-count">{description.length}/30</p>
                </div>
              </div>

              {/* CTA & URL */}
              <div className="wizard-two-col">
                <div>
                  <label className="wz-label">CTA Button</label>
                  <select className="wz-input" value={ctaButton} onChange={(e) => setCtaButton(e.target.value)} style={{ cursor: "pointer" }}>
                    {ctaOptions.map(cta => <option key={cta.id} value={cta.id}>{cta.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="wz-label">Destination URL</label>
                  <input className="wz-input" type="url" value={destinationUrl}
                    onChange={(e) => setDestinationUrl(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Live Preview */}
            <div className="wizard-preview-panel">
              <label className="wz-label wizard-preview-label">Ad Preview</label>
              <div className="wizard-ad-preview">
                <div className="wizard-preview-header">
                  <div className="wizard-preview-avatar" />
                  <div>
                    <div className="wizard-preview-page-name">Your Page Name</div>
                    <div className="wizard-preview-sponsored">Sponsored</div>
                  </div>
                </div>
                <div className="wizard-preview-text">
                  {primaryText || <span className="wizard-preview-text-placeholder">Your ad text will appear here...</span>}
                </div>
                <div className="wizard-preview-media">
                  {mediaPreview ? (
                    adMedia?.type?.startsWith("video/") ? (
                      <video src={mediaPreview} style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
                    ) : (
                      <img src={mediaPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    )
                  ) : (
                    <span className="wizard-preview-media-placeholder">Your media here</span>
                  )}
                </div>
                <div className="wizard-preview-footer">
                  <div style={{ flex: 1 }}>
                    {description && <div className="wizard-preview-footer-desc">{description}</div>}
                    <div className="wizard-preview-headline">
                      {headline || <span style={{ color: "#cbd5e1" }}>Headline</span>}
                    </div>
                  </div>
                  <div className="wizard-preview-cta">
                    {ctaOptions.find(c => c.id === ctaButton)?.label || "Learn More"}
                  </div>
                </div>
              </div>
              <p className="wizard-preview-note">Approximate preview — actual appearance may vary</p>
            </div>
          </div>
        )}

        {/* ─── STEP 4: BUDGET ─── */}
        {currentStep === 4 && (
          <div className="wizard-step-body">
            {/* Budget type */}
            <div className="wizard-budget-type-section">
              <label className="wz-label">Budget Type</label>
              <div className="wizard-budget-type-grid">
                {[
                  { id: "daily",    label: "Daily Budget" },
                  { id: "lifetime", label: "Lifetime Budget" },
                ].map(type => (
                  <div key={type.id}
                    className={`wizard-budget-type-card${budgetType === type.id ? " selected" : ""}`}
                    onClick={() => setBudgetType(type.id)}>
                    <div className="wizard-budget-type-name">{type.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="wz-label">{budgetType === "daily" ? "Daily" : "Lifetime"} Budget ($)</label>
              <div className="wizard-budget-input-wrap">
                <span className="wizard-currency-prefix">$</span>
                <input className="wz-input wz-input-budget" type="number" min="1"
                  value={campaignBudget} onChange={(e) => setCampaignBudget(Number(e.target.value))} />
              </div>
              {campaignBudget < 5 && (
                <p className="wizard-budget-warn">Facebook recommends a minimum of $5/day for best results</p>
              )}
            </div>

            {/* Schedule */}
            <div className="wizard-schedule-row">
              <div>
                <label className="wz-label">Start Date & Time</label>
                <DatePicker
                  selected={campaignStartDate ? new Date(campaignStartDate) : null}
                  onChange={(date) => setCampaignStartDate(date && !isNaN(date) ? toLocalISO(date) : "")}
                  showTimeSelect
                  dateFormat="MMM d, yyyy h:mm aa"
                  placeholderText="Select start date..."
                  className="wz-input wz-input-full"
                  wrapperClassName="wz-datepicker-wrap"
                  minDate={new Date()}
                  popperPlacement="bottom-start"
                />
              </div>
              <div>
                <label className="wz-label">End Date & Time <span style={{ color: "var(--color-text-muted)", fontWeight: 400 }}>(optional)</span></label>
                <DatePicker
                  selected={campaignEndDate ? new Date(campaignEndDate) : null}
                  onChange={(date) => setCampaignEndDate(date && !isNaN(date) ? toLocalISO(date) : "")}
                  showTimeSelect
                  dateFormat="MMM d, yyyy h:mm aa"
                  placeholderText="No end date (runs continuously)"
                  className="wz-input wz-input-full"
                  wrapperClassName="wz-datepicker-wrap"
                  minDate={(() => { const d = new Date(campaignStartDate || Date.now()); d.setDate(d.getDate() + 1); return d; })()}
                  popperPlacement="bottom-start"
                  isClearable
                />
                <div style={{ fontSize: "0.72rem", color: "var(--color-text-muted)", marginTop: 4 }}>Must be at least 24 hours after start — Meta needs time to review your ad before it runs.</div>
              </div>
            </div>

            {/* Estimate */}
            {campaignStartDate && campaignEndDate && budgetType === "daily" && (
              <div className="wizard-budget-estimate">
                <span>Estimated total spend: </span>
                <span className="wizard-budget-estimate-amt">
                  ${(campaignBudget * Math.max(1, Math.ceil((new Date(campaignEndDate) - new Date(campaignStartDate)) / (1000 * 60 * 60 * 24)))).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ─── STEP 5: REVIEW ─── */}
        {currentStep === 5 && (
          <div>
            <h3 style={{ fontSize: "1.2rem", fontWeight: 700, color: "var(--color-text)", marginBottom: "20px" }}>Review Your Campaign</h3>

            <div className="wizard-review-layout">
              {/* Left - Summary */}
              <div className="wizard-review-left">
                {/* Campaign */}
                <div className="wizard-review-section">
                  <div className="wizard-review-section-title">Campaign</div>
                  <div className="wizard-review-rows">
                    <div className="wizard-review-row">
                      <span className="wizard-review-row-label">Ad Account</span>
                      <span className="wizard-review-row-value">{fbAccounts[selectedFbAccountId]?.name || selectedFbAccountId || "—"}</span>
                    </div>
                    <div className="wizard-review-row">
                      <span className="wizard-review-row-label">Campaign Name</span>
                      <span className="wizard-review-row-value">{campaignTitle || "—"}</span>
                    </div>
                    <div className="wizard-review-row">
                      <span className="wizard-review-row-label">Ad Group</span>
                      <span className="wizard-review-row-value">{adGroupName || `${campaignTitle} - Ad Set`}</span>
                    </div>
                    <div className="wizard-review-row">
                      <span className="wizard-review-row-label">Objective</span>
                      <span className="wizard-review-row-value">{campaignObjectives.find(o => o.id === selectedObjective)?.label || "—"}</span>
                    </div>
                    <div className="wizard-review-row">
                      <span className="wizard-review-row-label">Platforms</span>
                      <div className="wizard-review-platform-badges">
                        {platforms.facebook && <span className="wizard-fb-badge">Facebook</span>}
                        {platforms.instagram && <span className="wizard-ig-badge">Instagram</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Audience */}
                <div className="wizard-review-section">
                  <div className="wizard-review-section-title">Audience</div>
                  <div className="wizard-review-rows">
                    <div className="wizard-review-row">
                      <span className="wizard-review-row-label">Location</span>
                      <span className="wizard-review-row-value">{locations.length > 0 ? locations.map(l => l.name).join(", ") : "—"}</span>
                    </div>
                    <div className="wizard-review-row">
                      <span className="wizard-review-row-label">Age</span>
                      <span className="wizard-review-row-value">{ageRange.min} – {ageRange.max}</span>
                    </div>
                    <div className="wizard-review-row">
                      <span className="wizard-review-row-label">Gender</span>
                      <span className="wizard-review-row-value" style={{ textTransform: "capitalize" }}>{gender}</span>
                    </div>
                    {interests.length > 0 && (
                      <div>
                        <span className="wizard-review-interest-label">Interests</span>
                        <div className="wizard-review-interests">
                          {interests.map(i => <span key={i.id} className="wizard-review-interest-tag">{i.name}</span>)}
                        </div>
                      </div>
                    )}
                    {selectedSavedAudienceId && (
                      <div>
                        <span className="wizard-review-interest-label">Preset</span>
                        <span className="wizard-review-interest-tag">
                          {savedAudiences.find(a => a.id === selectedSavedAudienceId)?.name || "Saved Audience"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Budget */}
                <div className="wizard-review-section">
                  <div className="wizard-review-section-title">Budget & Schedule</div>
                  <div className="wizard-review-rows">
                    <div className="wizard-review-row">
                      <span className="wizard-review-row-label">Budget</span>
                      <span className="wizard-review-row-value">${campaignBudget}/{budgetType === "daily" ? "day" : "total"}</span>
                    </div>
                    <div className="wizard-review-row">
                      <span className="wizard-review-row-label">Start</span>
                      <span className="wizard-review-row-value">{campaignStartDate || "Not set"}</span>
                    </div>
                    {campaignEndDate && (
                      <div className="wizard-review-row">
                        <span className="wizard-review-row-label">End</span>
                        <span className="wizard-review-row-value">{campaignEndDate}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right - Preview */}
              <div className="wizard-preview-panel">
                <div className="wizard-review-section-title" style={{ marginBottom: "10px" }}>Ad Preview</div>
                <div className="wizard-ad-preview">
                  <div className="wizard-preview-header">
                    <div className="wizard-preview-avatar" />
                    <div>
                      <div className="wizard-preview-page-name">Your Page Name</div>
                      <div className="wizard-preview-sponsored">Sponsored</div>
                    </div>
                  </div>
                  <div className="wizard-preview-text">{primaryText || "—"}</div>
                  <div className="wizard-preview-media">
                    {mediaPreview ? (
                      <img src={mediaPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <span className="wizard-preview-media-placeholder">No media</span>
                    )}
                  </div>
                  <div className="wizard-preview-footer">
                    <div style={{ flex: 1 }}>
                      {description && <div className="wizard-preview-footer-desc">{description}</div>}
                      <div className="wizard-preview-headline">{headline || "—"}</div>
                    </div>
                    <div className="wizard-preview-cta">
                      {ctaOptions.find(c => c.id === ctaButton)?.label || "Learn More"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {statusMessage && !fbCampaignId && (
              <div className={`wizard-status-message ${statusMessage.includes("Error") ? "error" : "success"}`}>
                {statusMessage}
              </div>
            )}
            {fbCampaignId && (
              <div className="wizard-status-message success">Campaign Created!</div>
            )}
          </div>
        )}
      </div>

      </div> {/* end wizard-modal-body */}

      {/* Footer — consistent Back / Continue navigation on every step */}
      <div className="wizard-modal-footer">
        {currentStep > 1 ? (
          <button className="wizard-back-btn" onClick={prevStep}>
            <FaChevronLeft style={{ fontSize: "0.75rem" }} /> Back
          </button>
        ) : <span />}

        {currentStep < steps.length ? (
          <button className="wizard-publish-btn" onClick={nextStep}>
            Continue <FaChevronRight style={{ fontSize: "0.75rem" }} />
          </button>
        ) : !isSubscribed ? (
          <div className="wizard-unsub-block">
            <span>A subscription is required to publish.</span>
            <button className="wizard-sub-btn" onClick={() => { onClose(); navigate("/dashboard?view=billing"); }}>Subscribe</button>
          </div>
        ) : (
          <button className="wizard-publish-btn" onClick={publishCampaign} disabled={isPublishing}>
            {isPublishing ? "Creating..." : "Create Campaign"}
          </button>
        )}
      </div>
    </div> {/* end wizard-modal */}
    </div> {/* end wizard-modal-wrap */}
    </div> {/* end wizard-overlay */}
    </>
  );
};

export default CreateCampaignWizard;
